import { useEffect, useState } from "react";
import { Controller } from "react-hook-form";

import { LABELS } from "~/resources/Labels";
import { MESSAGES } from "~/resources/Message";

import { SIMPLInput } from "../../components/commons/input/SIMPLInput";
import { SIMPLSelectDropdown } from "../../components/commons/input/SIMPLSelectDropdown";
import { SIMPLDialog } from "../../components/commons/dialog/SimplDialog";
import { SIMPLButtonCustomized } from "../../components/commons/button/SIMPLButtonCustumized";
import { SliderField } from "../../components/commons/slider/SIMPLSlider";

import { UseVmTemplateForm } from "./UseVmTemplateForm";

import {
  extractCloudProviderList,
  extractComponentsDetails,
  extractEnvDetails,
  getProviders,
} from "~/Utils/Utils";

import {
  useGetCloudProvisionerFullQuery,
  useGetCloudProvisionerQuery,
} from "~/services/cloudProvisioner";
import { CloudProvisionerResponse, OptionProp, TemplateResponse } from "~/models/Commons ";

// -------------------- Types --------------------
interface Prop {
  onCloseModal: () => void;
  refetch: () => void;
  editedItem: TemplateResponse | null;
  setConfig: Function;
  title?: string;
}

export const EditVmTemplate = ({
  onCloseModal,
  editedItem,
  setConfig,
  title,
  refetch,
}: Prop) => {
  const {
    handleSubmit,
    updateForm,
    control,
    errors,
    cloudEnvData,
    cloudProviderName,
    ConfigurationComponentsData,
    PolicyComponentsData,
    ProvissioningComponentsData,
    providerData,
    watch,
    resetField,
    setValue,
  } = UseVmTemplateForm(onCloseModal, refetch, editedItem?.id!);

  // -------------------- State --------------------
  const [cloudProviderList, setCloudProviderList] = useState<any[]>([]);
  const [cpu, setCpu] = useState<{ min: number; max: number } | null>(null);
  const [ram, setRam] = useState<{ min: number; max: number } | null>(null);
  const [showSubmit, setShowSubmit] = useState<boolean>(false);
  const [storage, setStorage] = useState<{ min: number; max: number } | null>(
    null
  );
  const [cloudProvisionerTemplateId, setCloudProvisionerTemplateId] = useState<
    number | undefined
  >(undefined);
  const [osOptions, setOsOptions] = useState<string[]>([]);
  const [selectClouldProvisioner, setSelectClouldProvisioner] = useState<any[]>(
    []
  );
    const [clouldProvisionerList, setClouldProvisionerList] = useState<any[]>(
      []
    );
  const [selectedCloudEnvironments, setSelectedCloudEnvironments] = useState<any[]>([]);
  const [selectCloudEnv, setSelectCloudEnv] = useState<any[]>([]);
  const [selectProvider, setSelectProvider] = useState<any[]>([]);
  const [selectVmConfiguration, setSelectVmConfiguration] = useState<any[]>([]);
  const [selectPostProvisioning, setSelectPostProvisioning] = useState<any[]>(
    []
  );
  const [selectPolicy, setSelectPolicy] = useState<any[]>([]);
  const [showAzureInput, setShowAzureInput] = useState<boolean>(false);

  const { data } = useGetCloudProvisionerFullQuery();
  const { data: provisionerData } = useGetCloudProvisionerQuery(
    cloudProvisionerTemplateId!,
    {
      skip: cloudProvisionerTemplateId === undefined,
    }
  );

  // -------------------- Handlers --------------------
  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleSubmit(updateForm)(event);
    setConfig({
      key: "",
      direction: LABELS.ASC,
    });
  };
   const getVmComponentsOptions = (components: { name: string; id: number }[]) => {
  return components.map((cp) => ({
    label: cp.name, 
    value: cp.id,
  }));
};
  const getCloudProviderOptions = (
    provider: { cloudProviderName: string; id: number }[]
  ) =>
    provider?.map((cloudEnv) => ({
      label: cloudEnv.cloudProviderName,
      value: cloudEnv.id,
    }));
   

  const handleCloudProviderChange = (
    value: number,
    onChange: (val: number) => void
  ) => {
    onChange(value);
    const selected = cloudProviderName?.find((p) => p.id === value);
    const isAzure = selected?.cloudProviderName === LABELS.Azure;
    setShowAzureInput(isAzure);

    if (isAzure) {
      resetField("os");
      resetField("cpuCore");
      resetField("ram");
      resetField("storage");
    } else {
      resetField("os");
    }
  };
const fetchProviderList = () => {
    setCloudProviderList(getProviders(providerData));
  };
  // -------------------- Init Template --------------------
  const initVmTemplate = () => {
    const payloadData = data?.payload as any | undefined;
    const provisionerPayload = provisionerData?.payload as
      | CloudProvisionerResponse
      | undefined;
    const envPayload = cloudEnvData?.payload as any | undefined;
    const configPayload = ConfigurationComponentsData?.payload as
      | any
      | undefined;
    const postProvisionPayload = ProvissioningComponentsData?.payload as
      | any
      | undefined;
    const policyPayload = PolicyComponentsData?.payload as any | undefined;

    if (payloadData?.content) {
      setClouldProvisionerList(payloadData?.content)
      setSelectClouldProvisioner(
        payloadData.content.map(({ id, fileName }: any) => ({ id, fileName }))
      );
    }

    if (envPayload) setSelectCloudEnv(extractEnvDetails(envPayload.content));
    const optionProvider = extractCloudProviderList(envPayload?.content);
    setSelectedCloudEnvironments(envPayload?.content)
    setSelectProvider(optionProvider);

    if (configPayload)
      setSelectVmConfiguration(extractComponentsDetails(configPayload.content));

    if (postProvisionPayload)
      setSelectPostProvisioning(
        extractComponentsDetails(postProvisionPayload.content)
      );

    if (policyPayload)
      setSelectPolicy(extractComponentsDetails(policyPayload.content));

    if (provisionerPayload) {
      const {
        minCpuCore,
        maxCpuCore,
        minRam,
        maxRam,
        minStorage,
        maxStorage,
        os,
      } = provisionerPayload;
      setCpu({ min: minCpuCore, max: maxCpuCore });
      setRam({ min: minRam, max: maxRam });
      setStorage({ min: minStorage, max: maxStorage });
      setOsOptions(os);
    }
  };

  // -------------------- Effects --------------------
  useEffect(() => {
    initVmTemplate();
    fetchProviderList()
  }, [
    data,
    provisionerData,
    cloudEnvData,
    ConfigurationComponentsData,
    PolicyComponentsData,
    providerData
  ]);

  const providerId: any = watch("providerType");
  useEffect(() => {
    fetchProviderList()
    if (providerId) setShowAzureInput(providerId.label === "Azure");
  }, [providerId]);
 // Watch the selected provider
const provider = watch("providerType");

// Update UI and reset environment when provider changes
useEffect(() => {
  if (!provider) return;
  setShowAzureInput(provider.label === "Azure");
}, [provider, setValue]);

// Filter environments based on selected provider
const filteredEnvironments = provider
  ? selectedCloudEnvironments?.filter(
      (env) => env.cloudProvider?.cloudProviderName === provider.label
    ) ?? []
  : selectCloudEnv;

  // Filter cloudProvisionerTemplate based on selected provider
const filteredCloudProvisionerTemplate = provider
  ? clouldProvisionerList?.filter((env) =>
      provider.label === "Azure"? env.isAzure : !env.isAzure
    ) ?? []
  : selectClouldProvisioner;
  const watchProvisioner = watch("cloudProvisionerTemplateId");
  useEffect(() => {
    setCloudProvisionerTemplateId(watchProvisioner?.value || undefined);
  }, [watchProvisioner, provisionerData]);

  // -------------------- Prefill edited item --------------------
  useEffect(() => {
    if (
      !editedItem ||
      !selectCloudEnv.length ||
      !selectProvider.length ||
      !selectClouldProvisioner.length
    )
      return;

    setValue("name", editedItem.name || "");
    setValue("description", editedItem.description || "");

    const env = selectCloudEnv.find(
      (e) => e.id === editedItem.cloudEnvironment.id
    );
    if (env)
      setValue("cloudEnvironmentId", {
        label: env.environmentName,
        value: env.id,
      });

    const provider = cloudProviderList?.find(
      (p) => p.id === editedItem.cloudEnvironment.cloudProvider.id
    );
  
    if (provider)
      setValue("providerType", {
        label: provider.cloudProviderName,
        value: provider.id,
      });

    const provisioner = selectClouldProvisioner.find(
      (p) => p.id === editedItem.cloudProvisionerTemplate.id
    );
    if (provisioner)
      setValue("cloudProvisionerTemplateId", {
        label: provisioner.fileName,
        value: provisioner.id,
      });

    if (Array.isArray(editedItem.components)) {
      editedItem.components.forEach((component) => {
        // VM Configuration
        const vmConf = selectVmConfiguration.find((c) => c.id === component.id);
        if (vmConf) {
          setValue("vmconfiguration", { label: vmConf.name, value: vmConf.id });
        }

        // Post Provisioning
        const postProv = selectPostProvisioning.find(
          (p) => p.id === component.id
        );
        if (postProv) {
          setValue("vmPostProviosiing", {
            label: postProv.name,
            value: postProv.id,
          });
        }

        // Policy
        const policy = selectPolicy.find((p) => p.id === component.id);
        if (policy) {
          setValue("vmPolicy", { label: policy.name, value: policy.id });
        }
      });
    } else {
      const component = editedItem.components;

      const vmConf = selectVmConfiguration.find((c) => c.id === component.id);
      if (vmConf) {
        setValue("vmconfiguration", { label: vmConf.name, value: vmConf.id });
      }

      const postProv = selectPostProvisioning.find(
        (p) => p.id === component.id
      );
      if (postProv) {
        setValue("vmPostProviosiing", {
          label: postProv.name,
          value: postProv.id,
        });
      }

      const policy = selectPolicy.find((p) => p.id === component.id);
      if (policy) {
        setValue("vmPolicy", { label: policy.name, value: policy.id });
      }
    }

    if (editedItem.os)
      setValue("os", { label: editedItem.os, value: editedItem.os });
    setValue("cpuCore", editedItem.cpuCore ?? 0);
    setValue("ram", editedItem.ram ?? 0);
    setValue("storage", editedItem.storage ?? 0);
  }, [
    editedItem,
    selectCloudEnv,
    cloudProviderList,
    selectClouldProvisioner,
    selectVmConfiguration,
    selectPostProvisioning,
    selectPolicy,
    setValue,
  ]);
 
  // -------------------- UI Helpers --------------------
  const renderButtons = () => (
    <>
      <div className="grow-0 basis-32 mr-5">
        <SIMPLButtonCustomized
          label="Cancel"
          secondary
          buttonHandlerAction={onCloseModal}
        />
      </div>
      <div className="grow-0 basis-32">
       {showSubmit && <SIMPLButtonCustomized label={LABELS.Submit} primary />}
      </div>
    </>
  );
  // -------------------- Render --------------------
  return (
    <SIMPLDialog
      onCloseModal={onCloseModal}
      title={title!}
      actions={renderButtons()}
      onSubmit={handleFormSubmit}
    >
      <div className="flex justify-center">
        <div className="w-[500px]">
          <div className="flex flex-col w-full gap-y-4">
            {/* Name */}
            <SIMPLInput
              name="name"
              control={control}
              rules={{
                required: { value: true, message: LABELS.NameRequired },
              }}
              label={LABELS.Name}
              placeholder={LABELS.Name}
              required
              id="name"
              readOnly
              fontWeight="bold"
              errorLabel={errors.name?.message}
            />

            {/* Description */}
            <SIMPLInput
              name="description"
              control={control}
              readOnly
              label={LABELS.Description}
              id="description"
              fontWeight="bold"
              errorLabel={errors.description?.message}
              placeholder={LABELS.Description}
            />
                {/* Provider */}
            <div className="h-auto">
              <Controller
                name="providerType"
                control={control}
                rules={{ required: MESSAGES.MessageCloudProviderRequired }}
                render={({ field }) => (
                  <SIMPLSelectDropdown
                    id="providerId"
                    label={LABELS.CloudProviderWithSpace}
                    options={getCloudProviderOptions(cloudProviderList)}
                    isRequired
                    isDisabled
                    value={field.value}
                    onChange={(val: any) =>
                      handleCloudProviderChange(val, field.onChange)
                    }
                    hideErrorLabel
                    errorLabel={errors.providerType?.message as string}
                    placeholder={LABELS.CloudProviderWithSpace}
                  />
                )}
              />
            </div>
            {/* Cloud Environment */}
            <div className="h-auto">
              <Controller
                name="cloudEnvironmentId"
                control={control}
                rules={{ required: MESSAGES.MessageCloudEnvironmentRequired }}
                render={({ field }) => (
                  <SIMPLSelectDropdown
                    id="cloudEnvironmentId"
                    label={LABELS.CloudEnviroment}
                    options={filteredEnvironments.map((cloudEnv) => ({
                      label: cloudEnv.environmentName,
                      value: cloudEnv.id,
                    }))}
                    isRequired
                    isDisabled
                    value={field.value}
                    onChange={field.onChange}
                    hideErrorLabel
                    errorLabel={errors.cloudEnvironmentId?.message as string}
                    placeholder={LABELS.CloudEnviroment}
                  />
                )}
              />
            </div>
           
            {/* Provisioning Template */}
            <div className="h-auto">
              <Controller
                name="cloudProvisionerTemplateId"
                control={control}
                rules={{
                  required: MESSAGES.MessageProvisioningTemplateRequired,
                }}
                render={({ field }) => (
                  <SIMPLSelectDropdown
                    id="cloudProvisionerTemplateId"
                    label={LABELS.ProvisioningTemplate}
                    options={filteredCloudProvisionerTemplate.map((template) => ({
                      label: template.fileName,
                      value: template.id,
                    }))}
                    isRequired
                    isDisabled
                    value={field.value}
                    onChange={field.onChange}
                    hideErrorLabel
                    errorLabel={
                      errors.cloudProvisionerTemplateId?.message as string
                    }
                    placeholder={LABELS.ProvisioningTemplate}
                  />
                )}
              />
            </div>

            {/* VM Configuration */}
            <div className="h-auto">
              <Controller
                name="vmconfiguration"
                control={control}
                rules={{
                  validate: (value) =>
                    value || watch("vmPostProviosiing") || watch("vmPolicy")
                      ? true
                      : MESSAGES.MessageVmConfigurationRequired,
                }}
                render={({ field }) => (
                  <SIMPLSelectDropdown
                    id="vmconfiguration"
                    label={LABELS.VmConfiguration}
                    options={getVmComponentsOptions(selectVmConfiguration)}
                      value={field.value || null} 
                      onChange={(selected: any) => {
                        field.onChange(selected ?? null);
                        setShowSubmit(true);
                      }}

                    hideErrorLabel
                    errorLabel={errors.vmconfiguration?.message as string}
                    placeholder={LABELS.VmConfiguration}
                  />
                )}
              />
            </div>

            {/* Post-Provisioning */}
            <div className="h-auto">
              <Controller
                name="vmPostProviosiing"
                control={control}
                rules={{
                  validate: (value) =>
                    value || watch("vmconfiguration") || watch("vmPolicy")
                      ? true
                      : MESSAGES.MessageVmPostProvisioningRequired,
                }}
                render={({ field }) => (
                  <SIMPLSelectDropdown
                    id="vmPostProviosiing"
                    label={LABELS.VmProvisioning}
                    options={getVmComponentsOptions(selectPostProvisioning)}
                      value={field.value || null} 
                      onChange={(selected: any) => {
                        field.onChange(selected ?? null);
                        setShowSubmit(true);
                      }}
                    hideErrorLabel
                    errorLabel={errors.vmPostProviosiing?.message as string}
                    placeholder={LABELS.VmProvisioning}
                  />
                )}
              />
            </div>

            {/* Policy */}
            <div className="h-auto">
              <Controller
                name="vmPolicy"
                control={control}
                rules={{
                  validate: (value) =>
                    value ||
                    watch("vmconfiguration") ||
                    watch("vmPostProviosiing")
                      ? true
                      : MESSAGES.MessageVmPolicyRequired,
                }}
                render={({ field }) => (
                  <SIMPLSelectDropdown
                    id="vmPolicy"
                    label={LABELS.VmPolicy}
                    options={getVmComponentsOptions(selectPolicy)}
                      value={field.value || null} 
                      onChange={(selected: any) => {
                        field.onChange(selected ?? null);
                        setShowSubmit(true);
                      }}
                    hideErrorLabel
                    errorLabel={errors.vmPolicy?.message as string}
                    placeholder={LABELS.VmPolicy}
                  />
                )}
              />
            </div>

            {/* OS / Resources OR Ovg inputs */}
            {!showAzureInput ? (
              <>
                {/* OS */}
                <div className="h-auto">
                  <Controller
                    name="os"
                    control={control}
                    rules={{
                      required: {
                        value: !showAzureInput,
                        message: MESSAGES.MessageOsRequired,
                      },
                    }}
                    render={({ field }) => (
                      <SIMPLSelectDropdown
                        id="os"
                        label={LABELS.Os}
                        options={osOptions.map((os) => ({
                          label: os,
                          value: os,
                        }))}
                        isDisabled
                        value={field.value}
                        onChange={field.onChange}
                        errorLabel={errors.os?.message as string}
                        placeholder={LABELS.SelectOs}
                        hideErrorLabel
                        isRequired={!showAzureInput}
                      />
                    )}
                  />
                </div>

                {/* CPU */}
                <div className="h-auto">
                  <Controller
                    name="cpuCore"
                    control={control}
                    rules={{
                      required: {
                        value: !showAzureInput,
                        message: MESSAGES.MessageCpuCoreRequired,
                      },
                    }}
                    render={({ field }) => (
                      <SliderField
                        id="cpuCores"
                        label={LABELS.CpuCore}
                        value={field.value}
                        disabled
                        min={cpu?.min}
                        max={cpu?.max}
                        valueLabel={LABELS.CpuCoreValue}
                        onChange={field.onChange}
                        errorLabel={errors.cpuCore?.message as string}
                        required={!showAzureInput}
                      />
                    )}
                  />
                </div>

                {/* RAM */}
                <div className="h-auto">
                  <Controller
                    name="ram"
                    control={control}
                    rules={{
                      required: {
                        value: !showAzureInput,
                        message: MESSAGES.MessageRamRequired,
                      },
                    }}
                    render={({ field }) => (
                      <SliderField
                        id="ram"
                        label={LABELS.Ram}
                        disabled
                        min={ram?.min}
                        max={ram?.max}
                        value={field.value}
                        valueLabel={LABELS.RamValue}
                        onChange={field.onChange}
                        errorLabel={errors.ram?.message as string}
                        required={!showAzureInput}
                      />
                    )}
                  />
                </div>

              </>
            ) : (
              <>
                {/* OS */}
                <div className="h-auto">
                  <Controller
                    name="os"
                    control={control}
                    rules={{
                      required: {
                        value: !showAzureInput,
                        message: MESSAGES.MessageOsRequired,
                      },
                    }}
                    render={({ field }) => (
                      <SIMPLSelectDropdown
                        id="os"
                        label={LABELS.Os}
                        options={osOptions.map((os) => ({
                          label: os,
                          value: os,
                        }))}
                        value={field.value}
                        isDisabled
                        onChange={field.onChange}
                        errorLabel={errors.os?.message as string}
                        placeholder={LABELS.SelectOs}
                        hideErrorLabel
                        isRequired={!showAzureInput}
                      />
                    )}
                  />
                </div>
              </>
            )}

            {/* Disk Size (GB) — common to all cloud providers (read-only) */}
            <div className="h-auto">
              <SIMPLInput
                name="storage"
                control={control}
                type="number"
                rules={{
                  min: {
                    value: Math.max(30, storage?.min ?? 30),
                    message: `Disk size must be ≥ ${Math.max(30, storage?.min ?? 30)} GB`,
                  },
                  ...(storage?.max != null && {
                    max: { value: storage.max, message: `Disk size must be ≤ ${storage.max} GB` },
                  }),
                }}
                label={LABELS.DiskSize}
                placeholder={LABELS.DiskSize}
                id="storage"
                fontWeight="bold"
                readOnly
                disabled
                errorLabel={errors.storage?.message as string}
              />
            </div>
          </div>
        </div>
      </div>
    </SIMPLDialog>
  );
};
